import { Component, Inject, OnInit, ViewChild, inject } from "@angular/core";
import { BootstrapService } from "../../shared/bootstrap/bootstrap.service";
import { FormBuilder, FormControl, FormGroup, NgForm, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin, Observable } from "rxjs";
import { debounceTime, finalize, map, switchMap, tap } from "rxjs/operators";
import { LocalStorageService } from "../../shared/local-storage.service";
import { FlightAttribute, FlightsService } from "../../flights-calendar/flights.service";
import moment from "moment";
import { Airport } from "../../shared/models/airport.model";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { Flight } from "../../shared/models/flight.model";
import { SharedService } from "../../shared/shared.service";
import { PilotRole } from "../../shared/models/pilotRole.model";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";

interface ChipItem {
    id: number;
    name: string;
}

@Component({
    selector: "app-update-flight-dialog",
    templateUrl: "./update-flight-dialog.component.html",
    styleUrls: ["./update-flight-dialog.component.scss"],
    standalone: false,
})
export class UpdateFlightDialogComponent implements OnInit {
    private readonly _bootstrapService = inject(BootstrapService);

    @ViewChild("flightForm") flightForm: NgForm;

    public appearanceType = "legacy";

    public parentFlight = true;
    public parentFlightCode = "";
    public minArrivalDate = new Date();
    public flightStatus = 2;

    public flightFormGroup: FormGroup;
    public passengerFormGroup: FormGroup;
    public flightCodeFormControl = new FormControl("", Validators.required);

    public pilotOptions: string[] = [];
    public filteredPilotOptions: Observable<string[]>;

    public filteredDepartureAirportOptions: Airport[];
    public filteredArrivalAirportOptions: Airport[];
    public filteredFlightCodeOptions: { id: number; code: string }[];

    public helicopterArray: ChipItem[] = [];
    public pilotArray: ChipItem[] = [];
    public flightAttr: { flightOperations: FlightAttribute[]; flightTypes: FlightAttribute[] };
    public pilotRoles: PilotRole[] = [];

    public loading = true;
    public departureIsLoading = false;
    public arrivalIsLoading = false;
    public flightCodeIsLoading = false;
    public positioningFlight = 0;

    constructor(
        public dialogRef: MatDialogRef<UpdateFlightDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        private _flightObject: {
            flightDate: Date;
            pilotsArray: ChipItem[];
            heliArray: ChipItem[];
            parentFlightData: { parentFlightCode: string; parentFlightId: number };
        },
        private _formBuilder: FormBuilder,
        private _snackBar: MatSnackBar,
        private _translate: TranslateService,
        private _flightsService: FlightsService,
        private _localStorageService: LocalStorageService,
        private _sharedService: SharedService,
    ) {
        this.flightFormGroup = this._formBuilder.group({
            id: [null],
            departureId: [""],
            arrivalId: [""],
            departurePlace: ["", Validators.required],
            arrivalPlace: ["", Validators.required],
            departureTime: ["", [Validators.required, Validators.pattern(/((?:[01]\d|2[0-3]):[0-5]\d$)/g)]],
            arrivalTime: ["", [Validators.required, Validators.pattern(/((?:[01]\d|2[0-3]):[0-5]\d$)/g)]],
            // flightDate: [this._flightObject.flightDate, Validators.required],
            // arrivalDate: [this._flightObject.flightDate, Validators.required],
            flightDate: [new Date(), Validators.required],
            arrivalDate: [new Date(), Validators.required],
            flightTypeId: ["", Validators.required],
            duration: [{ value: "", disabled: true }, Validators.required],
            pilotId: [""],
            pilotRoleId: [""],
            coPilotId: [""],
            coPilotName: [""],
            coPilotRoleId: [""],
            helicopterId: [""],
            passengers: [0, [Validators.min(0), Validators.required]],
            notes: [""],
            clientName: ["", Validators.required],
            pilotNotes: [""],
            parentId: [0],
            sourceType: [1],
            status: [1, Validators.required],
            isPositioning: [this.positioningFlight],
        });
        this.minArrivalDate = this._flightObject.flightDate;
        this.helicopterArray = this._flightObject.heliArray;
        this.pilotArray = this._flightObject.pilotsArray;
        this.pilotArray.map((pilot) => this.pilotOptions.push(pilot.name));
        this.pilotRoles = this._bootstrapService.pilotRoles();
        if (this._flightObject.parentFlightData) {
            this.parentFlightCode = this._flightObject.parentFlightData.parentFlightCode;
            this.parentFlight = false;
            this.flightCodeFormControl.setValue(this.parentFlightCode, { emitEvent: false });
            const parentFlight = this._flightsService.getFlights().filter((f) => f.id === this._flightObject.parentFlightData.parentFlightId)[0];
            this.flightFormGroup.patchValue({
                parentId: this._flightObject.parentFlightData.parentFlightId,
                pilotId: parentFlight.pilotId,
                pilotRoleId: parentFlight.pilotRoleId,
                coPilotId: parentFlight.coPilotId,
                coPilotName: parentFlight.coPilotName,
                coPilotRoleId: parentFlight.coPilotRoleId,
                helicopterId: parentFlight.helicopterId,
                clientName: parentFlight.clientName,
                flightTypeId: parentFlight.flightTypeId,
                passengers: parentFlight.passengers,
                notes: parentFlight.notes,
                pilotNotes: parentFlight.pilotNotes,
            });
        }
    }

    ngOnInit(): void {
        this._flightsService.getFlightAttributes().subscribe((result) => {
            this.flightAttr = result;

            this.filteredPilotOptions = this.flightFormGroup.get("coPilotName").valueChanges.pipe(map((value) => this._filterPilot(value)));

            /** FILTER FLIGHT CODE */
            this.flightCodeFormControl.valueChanges
                .pipe(
                    debounceTime(300),
                    tap(() => (this.flightCodeIsLoading = true)),
                    switchMap((value) => this._flightsService.searchFlightsByCode({ name: value }).pipe(finalize(() => (this.flightCodeIsLoading = false)))),
                )
                .subscribe((res) => (this.filteredFlightCodeOptions = res));
            /** END */

            this.flightFormGroup.valueChanges.subscribe((value) => {
                if (value.departureTime && value.arrivalTime) {
                    const arrivalDepartureTime = this._getArrivalTimeAndDepartureTimeObject(
                        this.flightFormGroup.get("flightDate").value,
                        this.flightFormGroup.get("arrivalDate").value,
                        value.arrivalTime,
                        value.departureTime,
                    );
                    if (arrivalDepartureTime.arrivalTime.isBefore(arrivalDepartureTime.departureTime)) {
                        this.flightFormGroup.get("arrivalTime").setErrors({ isBeforeError: true });
                        this.flightFormGroup.get("arrivalTime").markAsTouched();
                        return;
                    }
                    const ms = moment(arrivalDepartureTime.arrivalTime, "YYYY-MM-DD HH:mm:ss").diff(moment(arrivalDepartureTime.departureTime, "YYYY-MM-DD HH:mm:ss"));
                    const d = moment.duration(ms);
                    const s = Math.floor(d.asHours()) + moment.utc(ms).format(":mm");
                    this.flightFormGroup.patchValue({ duration: s }, { emitEvent: false });
                }
                if (value.departureTime && !value.arrivalTime) {
                    console.log(this.flightFormGroup.get("departureTime").value);
                    this.flightFormGroup.patchValue({ arrivalTime: this.flightFormGroup.get("departureTime").value });
                }
            });

            this.flightFormGroup.get("flightDate").valueChanges.subscribe((date) => {
                this.minArrivalDate = date;
                this.flightFormGroup.patchValue({ arrivalDate: date });
            });

            this.loading = false;
        });
    }

    private _getArrivalTimeAndDepartureTimeObject(
        flightDate: string,
        arrivalDate: string,
        arrivalTimeValue: string,
        departureTimeValue: string,
    ): { departureTime: any; arrivalTime: any } {
        const flightDateMoment = moment(flightDate).format("YYYY-MM-DD");
        const arrivalDateMoment = moment(arrivalDate).format("YYYY-MM-DD");
        const departureTime = moment(flightDateMoment + " " + departureTimeValue, "YYYY-MM-DD HH:mm:ss");
        const arrivalTime = moment(arrivalDateMoment + " " + arrivalTimeValue, "YYYY-MM-DD HH:mm:ss");
        return { departureTime: departureTime, arrivalTime: arrivalTime };
    }

    onFlightCodeOptionSelected(event: MatAutocompleteSelectedEvent) {
        const object = event.option.value as { id: number; code: string };
        if (object) {
            this.flightCodeFormControl.setValue(object.code);
            this.flightFormGroup.patchValue({ parentId: object.id }, { emitEvent: false });
        }
    }

    onFormSubmit() {
        this.flightFormGroup.get("coPilotRoleId").setErrors(null);
        this.flightFormGroup.get("pilotRoleId").setErrors(null);
        this.flightCodeFormControl.setErrors(null);
        if (this.flightFormGroup.get("coPilotName").value !== "") {
            if (this.flightFormGroup.get("coPilotRoleId").value === "") {
                this.flightFormGroup.get("coPilotRoleId").setErrors({ required: true });
                this.flightFormGroup.get("coPilotRoleId").markAsTouched();
            }
        }
        if (this.flightFormGroup.get("pilotId").value !== "") {
            if (this.flightFormGroup.get("pilotRoleId").value === "") {
                this.flightFormGroup.get("pilotRoleId").setErrors({ required: true });
                this.flightFormGroup.get("pilotRoleId").markAsTouched();
            }
        }
        const flightDate = moment(this.flightFormGroup.get("flightDate").value);
        const arrivalDate = moment(this.flightFormGroup.get("arrivalDate").value);
        if (arrivalDate.isBefore(flightDate)) {
            this.flightFormGroup.get("arrivalDate").setErrors({ beforeError: true });
            this.flightFormGroup.get("arrivalDate").markAsTouched();
        }

        if (!this.parentFlight && this.flightCodeFormControl.value === "") {
            this.flightCodeFormControl.setErrors({ required: true });
            this.flightCodeFormControl.markAsTouched();
        }

        if (this.flightForm.valid) {
            const coPilotId = this._getCoPilotId();
            const flightObject = { ...this.flightFormGroup.getRawValue() } as Flight;
            flightObject.flightDate = moment(flightObject.flightDate).format("YYYY-MM-DD");
            flightObject.arrivalDate = moment(flightObject.arrivalDate).format("YYYY-MM-DD");
            flightObject.coPilotId = coPilotId ? coPilotId.id : null;
            flightObject.status = this.flightStatus;
            flightObject.isPositioning = this.positioningFlight ? 1 : 0;
            this._flightsService.createNewFlight(flightObject).subscribe(() => {
                this.dialogRef.close("saved");
                this._snackBar.open(this._translate.instant("GENERAL.savedSuccessMsg"), this._translate.instant("GENERAL.close"), {
                    duration: 3000,
                });
            });
        } else {
            this._sharedService.checkIfFormGroupHasError(this.flightFormGroup).map((error) => {
                this.flightFormGroup.get(error.key).setErrors({ required: true });
                this.flightFormGroup.get(error.key).markAsTouched();
            });
        }
    }

    private _getCoPilotId() {
        if (!this.flightFormGroup.get("coPilotName").value) {
            return "";
        }
        const coPilotName = this.flightFormGroup.get("coPilotName").value.toLocaleLowerCase();
        return this.pilotArray.filter((pilot) => {
            const objCoPilotName = pilot.name.toLocaleLowerCase();
            return objCoPilotName === coPilotName;
        })[0];
    }

    private _filterPilot(value: string): string[] {
        const filterValue = value.toLowerCase();
        return this.pilotOptions.filter((option) => option.toLowerCase().indexOf(filterValue) === 0);
    }
}
